import type { DefaultProps, Accessor, AccessorFunction, Color } from '@deck.gl/core';
import { CompositeLayer } from '@deck.gl/core';
import type { ScatterplotLayerProps } from '@deck.gl/layers';
import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';

import RoundedRectangleLayer from '../RoundedRectangleLayer';
import { MapData } from '../../common/map-data';

const DEFAULT_COLOR: [number, number, number, number] = [0, 0, 0, 255];

export enum MapMarkerType {
  Circle = 'circle',
  Square = 'square',
}

export interface LabeledScatterplotLayerProps<DataT = MapData> extends ScatterplotLayerProps<DataT> {
  // Marker
  markerType?: MapMarkerType;
  cornerRadius?: number;
  // TextLayer props
  getText?: AccessorFunction<DataT, string>;
  getTextColor?: Accessor<DataT, Color>;
  getTextSize?: Accessor<DataT, number>;
  getTextAnchor?: Accessor<DataT, 'start' | 'middle' | 'end'>;
  getTextAlignmentBaseline?: Accessor<DataT, 'top' | 'center' | 'bottom'>;
}

const defaultProps: DefaultProps<LabeledScatterplotLayerProps> = {
  // Marker
  markerType: MapMarkerType.Circle,
  cornerRadius: { type: 'number', value: 0.4, min: 0, max: 1 },
  // ScatterplotLayer props
  radiusUnits: 'meters',
  radiusScale: { type: 'number', min: 0, value: 1 },
  radiusMinPixels: { type: 'number', min: 0, value: 0 }, //  min point radius in pixels
  radiusMaxPixels: { type: 'number', min: 0, value: Number.MAX_SAFE_INTEGER }, // max point radius in pixels
  lineWidthUnits: 'meters',
  lineWidthScale: { type: 'number', min: 0, value: 1 },
  lineWidthMinPixels: { type: 'number', min: 0, value: 0 },
  lineWidthMaxPixels: {
    type: 'number',
    min: 0,
    value: Number.MAX_SAFE_INTEGER,
  },
  stroked: true,
  filled: true,
  billboard: false,
  antialiasing: true,
  getPosition: { type: 'accessor', value: (x) => x.position },
  getRadius: { type: 'accessor', value: 10 },
  getPolygonOffset: () => [0, -100],
  getFillColor: { type: 'accessor', value: DEFAULT_COLOR },
  getLineColor: { type: 'accessor', value: DEFAULT_COLOR },
  getLineWidth: { type: 'accessor', value: 1 },
  // TextLayer props
  getTextSize: { type: 'accessor', value: 8 },
  getTextColor: [0, 0, 0], // Black text
  getTextAnchor: 'middle',
  getTextAlignmentBaseline: 'center',
};

export default class LabeledScatterplotLayer<DataT = unknown> extends CompositeLayer<
  LabeledScatterplotLayerProps<DataT>
> {
  static layerName = 'LabeledScatterplotLayer';
  static defaultProps = defaultProps;
  renderLayers() {
    const {
      data,
      markerType,
      cornerRadius,
      getPosition,
      getPolygonOffset,
      getRadius,
      getFillColor,
      getLineColor,
      stroked,
      lineWidthMinPixels,
      lineWidthMaxPixels,
      lineWidthScale,
      getLineWidth,
      radiusScale,
      radiusMinPixels,
      radiusMaxPixels,
      onClick,
      opacity,
      radiusUnits,
      getText,
      getTextSize,
      getTextColor,
      getTextAnchor,
      getTextAlignmentBaseline,
    } = this.props;
    const layers = [];
    if (markerType === MapMarkerType.Square) {
      layers.push(
        new RoundedRectangleLayer(
          this.getSubLayerProps({
            id: `${this.id}-rounded-rectangle`,
            data,
            getPosition,
            getPolygonOffset,
            getRadius,
            getFillColor,
            getLineColor,
            stroked,
            lineWidthMinPixels,
            lineWidthMaxPixels,
            lineWidthScale,
            getLineWidth,
            radiusScale,
            radiusMinPixels,
            radiusMaxPixels,
            cornerRadius,
            onClick,
            opacity,
            pickable: true,
            radiusUnits,
            updateTriggers: {
              getPosition: this.props.updateTriggers.getPosition,
              getRadius: this.props.updateTriggers.getRadius,
              getFillColor: this.props.updateTriggers.getFillColor,
              getLineColor: this.props.updateTriggers.getLineColor,
            },
          })
        )
      );
    } else {
      layers.push(
        new ScatterplotLayer(
          this.getSubLayerProps({
            id: `${this.id}-scatterplot`,
            data,
            getPosition,
            getPolygonOffset,
            getRadius,
            getFillColor,
            getLineColor,
            stroked,
            lineWidthMinPixels,
            lineWidthMaxPixels,
            lineWidthScale,
            getLineWidth,
            radiusScale,
            radiusMinPixels,
            radiusMaxPixels,
            onClick,
            opacity,
            pickable: true,
            radiusUnits,
            updateTriggers: {
              getPosition: this.props.updateTriggers.getPosition,
              getRadius: this.props.updateTriggers.getRadius,
              getFillColor: this.props.updateTriggers.getFillColor,
              getLineColor: this.props.updateTriggers.getLineColor,
            },
          })
        )
      );
    }
    layers.push(
      new TextLayer(
        this.getSubLayerProps({
          id: `${this.id}-text`,
          data,
          pickable: false,
          getPosition,
          getPolygonOffset,
          getText: getText,
          getSize: getTextSize,
          getColor: getTextColor,
          getAnchor: getTextAnchor,
          getAlignmentBaseline: getTextAlignmentBaseline,
          fontFamily: 'Roboto, Helvetica Neue, sans-serif',
          updateTriggers: {
            getPosition: this.props.updateTriggers.getPosition,
            getText: this.props.updateTriggers.getText,
            getSize: this.props.updateTriggers.getTextSize,
            getColor: this.props.updateTriggers.getTextColor,
            getAnchor: this.props.updateTriggers.getTextAnchor,
            getAlignmentBaseline: this.props.updateTriggers.getTextAlignmentBaseline,
          },
        })
      )
    );
    return layers;
  }
}
